<template>
  <div class="page-container">
    <el-card>
      <template #header>
        <div class="card-header">
          <div style="display: flex; align-items: center; gap: 12px;">
            <el-button @click="goBack" circle>
              <el-icon><Back /></el-icon>
            </el-button>
            <span style="font-size: 18px; font-weight: 500;">{{ $t('vip.vipDetails') }}</span>
          </div>
          
          <div class="header-actions" v-if="vipPurchases.length > 0">
            <el-dropdown @command="handleExportCommand" trigger="click">
              <el-button type="success" size="small">
                <el-icon><Download /></el-icon>
                {{ $t('common.export') }}
                <el-icon class="el-icon--right"><ArrowDown /></el-icon>
              </el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="xlsx">
                    <el-icon><Document /></el-icon>
                    Export as Excel (.xlsx)
                  </el-dropdown-item>
                  <el-dropdown-item command="csv">
                    <el-icon><Tickets /></el-icon>
                    Export as CSV (.csv)
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            
            <el-button-group>
              <el-button 
                :type="purchaseViewMode === 'list' ? 'primary' : ''" 
                @click="purchaseViewMode = 'list'"
                size="small"
              >
                <el-icon><List /></el-icon>
              </el-button>
              <el-button 
                :type="purchaseViewMode === 'card' ? 'primary' : ''" 
                @click="purchaseViewMode = 'card'"
                size="small"
              >
                <el-icon><Grid /></el-icon>
              </el-button>
            </el-button-group>
          </div>
        </div>
      </template>

      <div v-loading="loading" class="vip-detail">
        <!-- Loading Skeleton -->
        <template v-if="loading">
          <div class="loading-skeleton">
            <el-skeleton :rows="3" animated />
            <el-divider />
            <el-skeleton :rows="2" animated />
            <el-skeleton :rows="4" animated />
          </div>
        </template>

        <!-- Content when loaded -->
        <template v-else>
        <div v-if="customer" class="customer-info">
          <h2 style="margin: 0 0 20px 0;">{{ $t('vip.vipInformation') }}</h2>
          
          <div class="customer-main-content">
            <!-- Unified Customer & VIP Info Section -->
            <div class="customer-info-section">
              <div class="customer-header">
                <div class="customer-avatar" @click="previewAvatar(customer.avatar)">
                  <img v-if="customer.avatar" :src="customer.avatar" alt="Avatar" class="avatar-image" />
                  <i v-else class="iconfont icon-helmet avatar-icon"></i>
                </div>
                <div class="customer-details">
                  <h3>{{ customer.name }}</h3>
                  <a :href="`tel:${customer.phone}`" class="customer-phone">
                    <el-icon><Phone /></el-icon>
                    <span>{{ customer.phone }}</span>
                  </a>
                  <div class="status-badge" :class="getStatusClass(customer.status)">
                    <el-icon v-if="customer.status === 1"><CircleCheck /></el-icon>
                    <el-icon v-else><InfoFilled /></el-icon>
                    <span>{{ getStatusLabel(customer.status) }}</span>
                  </div>
                </div>
              </div>

              <el-divider />

              <!-- VIP Summary Section -->
              <div v-if="vipData" class="vip-section">
                <div class="section-title">
                  <el-icon :size="18"><Star /></el-icon>
                  <span>{{ $t('vip.vipSummary') }}</span>
                </div>
                
                <div class="vip-summary-cards">
                  <div class="summary-item">
                    <div class="summary-label">{{ $t('vip.totalPurchases') }}</div>
                    <div class="summary-value">{{ vipData.totalPurchases }}</div>
                  </div>
                  
                  <div class="summary-item">
                    <div class="summary-label">{{ $t('vip.activePurchases') }}</div>
                    <div class="summary-value success">{{ vipData.activePurchases }}</div>
                  </div>
                  
                  <div class="summary-item">
                    <div class="summary-label">{{ $t('vip.totalSpent') }}</div>
                    <div class="summary-value price">¥{{ vipData.totalSpent?.toFixed(2) || '0.00' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <el-divider />

          <div v-if="!vipData" class="no-vip">
            <el-empty :description="$t('vip.notVipMember')" />
          </div>

          <!-- VIP Purchase History -->
          <div v-if="vipPurchases.length > 0" class="purchase-history">
            <div class="section-title">
              <el-icon :size="20"><Clock /></el-icon>
              <span>{{ $t('vip.purchaseHistory') }}</span>
            </div>
            
            <!-- Desktop Table View -->
            <el-table v-show="purchaseViewMode === 'list'" :data="vipPurchases" stripe style="margin-top: 16px;" class="desktop-table">
              <el-table-column prop="packageName" :label="$t('vip.packageName')" min-width="150" show-overflow-tooltip />
              <el-table-column prop="totalHours" :label="$t('vip.totalHours')" width="90" align="center" />
              <el-table-column prop="remainingHours" :label="$t('vip.remainingHours')" width="120" align="center">
                <template #default="{ row }">
                  <span :class="{ 'text-warning': row.remainingHours < 5 }">
                    {{ row.remainingHours }} / {{ row.totalHours }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="paidAmount" :label="$t('vip.price')" width="100" align="right">
                <template #default="{ row }">
                  ¥{{ row.paidAmount.toFixed(2) }}
                </template>
              </el-table-column>
              <el-table-column prop="daysRemaining" :label="$t('vip.daysRemaining')" width="110" align="center">
                <template #default="{ row }">
                  <span :class="{ 'text-warning': row.daysRemaining < 7 && row.daysRemaining > 0 }">
                    {{ row.daysRemaining }} {{ $t('vip.days') }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column :label="$t('common.status')" width="100" align="center">
                <template #default="{ row }">
                  <el-tag :type="row.isActive ? 'success' : 'info'" size="small">
                    {{ row.status || (row.isActive ? $t('vip.active') : $t('vip.inactive')) }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="purchaseDate" :label="$t('vip.purchaseDate')" width="140" align="center">
                <template #default="{ row }">
                  <span class="purchase-date">{{ formatSimpleDate(row.purchaseDate) }}</span>
                </template>
              </el-table-column>
            </el-table>
            
            <!-- Mobile Card View -->
            <div v-show="purchaseViewMode === 'card'" class="mobile-cards">
              <el-card v-for="(purchase, index) in vipPurchases" :key="index" shadow="hover" class="purchase-card">
                <div class="purchase-card-header">
                  <div class="purchase-package">{{ purchase.packageName }}</div>
                  <el-tag :type="purchase.isActive ? 'success' : 'info'" size="small">
                    {{ purchase.status || (purchase.isActive ? $t('vip.active') : $t('vip.inactive')) }}
                  </el-tag>
                </div>
                <div class="purchase-card-body">
                  <div class="purchase-info-row">
                    <span class="label">{{ $t('vip.purchaseDate') }}</span>
                    <span class="value">{{ formatSimpleDate(purchase.purchaseDate) }}</span>
                  </div>
                  <div class="purchase-info-row">
                    <span class="label">{{ $t('vip.totalHours') }}</span>
                    <span class="value">{{ purchase.totalHours }}</span>
                  </div>
                  <div class="purchase-info-row">
                    <span class="label">{{ $t('vip.remainingHours') }}</span>
                    <span class="value" :class="{ 'text-warning': purchase.remainingHours < 5 }">
                      {{ purchase.remainingHours }} / {{ purchase.totalHours }}
                    </span>
                  </div>
                  <div class="purchase-info-row">
                    <span class="label">{{ $t('vip.price') }}</span>
                    <span class="value price">¥{{ purchase.paidAmount.toFixed(2) }}</span>
                  </div>
                  <div class="purchase-info-row">
                    <span class="label">{{ $t('vip.daysRemaining') }}</span>
                    <span class="value" :class="{ 'text-warning': purchase.daysRemaining < 7 && purchase.daysRemaining > 0 }">
                      {{ purchase.daysRemaining }} {{ $t('vip.days') }}
                    </span>
                  </div>
                </div>
              </el-card>
            </div>
          </div>
        </div>

        <div v-else class="error-message">
          <el-result icon="error" :title="$t('vip.customerNotFound')" :sub-title="$t('vip.customerNotExist')">
            <template #extra>
              <el-button type="primary" @click="goBack">{{ $t('vip.backToCustomers') }}</el-button>
            </template>
          </el-result>
        </div>
        </template>
      </div>
    </el-card>

    <!-- Image Preview Dialog -->
    <el-dialog v-model="showImagePreview" title="Avatar Preview" width="500px" center>
      <div style="text-align: center;">
        <img :src="previewImageUrl" alt="Avatar Preview" style="max-width: 100%; max-height: 80vh; border-radius: 8px;" />
      </div>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Star, Back, Clock, Check, Phone, CircleCheck, InfoFilled, List, Grid, Download, ArrowDown, Document, Tickets } from '@element-plus/icons-vue'
import { getCustomerDetail } from '@/api/customer'
import { checkCustomerVipState } from '@/api/vip'
import { exportToExcel, exportToCSV } from '@/utils/export'
import { ElMessage } from 'element-plus'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const loading = ref(false)
const customer = ref(null)
const vipData = ref(null)
const vipPurchases = ref([])
const purchaseViewMode = ref('card') // 'card' or 'list'
const showImagePreview = ref(false)
const previewImageUrl = ref('')

const customerId = route.params.id

const loadCustomerData = async () => {
  loading.value = true
  
  try {
    // Load customer details
    const customerRes = await getCustomerDetail(customerId)
    if (customerRes.success) {
      customer.value = customerRes.data
      
      // Load VIP data (includes purchases and summary)
      const vipRes = await checkCustomerVipState(customerId)
      if (vipRes.success && vipRes.data) {
        vipData.value = vipRes.data
        // Extract purchases array from the response
        vipPurchases.value = vipRes.data.purchases || []
      }
    }
  } catch (error) {
    console.error('Failed to load customer VIP data:', error)
    customer.value = null
  } finally {
    loading.value = false
  }
}

const formatSimpleDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const getStatusLabel = (status) => {
  const statusMap = {
    0: t('customer.statusOptions.inactive'),
    1: t('customer.statusOptions.active'),
    2: t('customer.statusOptions.deleted'),
    3: t('customer.statusOptions.blocked')
  }
  return statusMap[status] || 'Unknown'
}

const getStatusClass = (status) => {
  const classMap = {
    0: 'status-inactive',
    1: 'status-active',
    2: 'status-deleted',
    3: 'status-blocked'
  }
  return classMap[status] || 'status-inactive'
}

const goBack = () => {
  // Use router back to return to previous page
  router.back()
}

const previewAvatar = (avatarUrl) => {
  if (!avatarUrl) return
  previewImageUrl.value = avatarUrl
  showImagePreview.value = true
}

const handleExportCommand = (format) => {
  if (format === 'csv') {
    handleExport('csv')
  } else {
    handleExport('xlsx')
  }
}

const handleExport = (format = 'xlsx') => {
  try {
    if (vipPurchases.value.length === 0) {
      ElMessage.warning(t('common.noData'))
      return
    }
    
    // Transform data for export
    const exportData = vipPurchases.value.map(purchase => ({
      'Package Name': purchase.packageName || '',
      'Total Hours': purchase.totalHours || 0,
      'Remaining Hours': purchase.remainingHours || 0,
      'Price': `¥${purchase.paidAmount.toFixed(2)}`,
      'Days Remaining': `${purchase.daysRemaining} days`,
      'Status': purchase.status || (purchase.isActive ? 'Active' : 'Expired'),
      'Purchase Date': purchase.purchaseDate ? formatSimpleDate(purchase.purchaseDate) : ''
    }))
    
    // Export based on format
    const fileName = `vip-purchases-${customer.value?.name || 'unknown'}-${Date.now()}`
    if (format === 'csv') {
      exportToCSV(exportData, fileName)
    } else {
      exportToExcel(exportData, fileName, 'VIP Purchases')
    }
    
    ElMessage.success(t('common.exportSuccess'))
  } catch (error) {
    console.error('Export failed:', error)
    ElMessage.error(error.message || t('common.exportFailed'))
  }
}

onMounted(() => {
  loadCustomerData()
})
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.vip-detail {
  max-width: 900px;
  margin: 0 auto;
}

.customer-main-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  align-items: start;
  margin-bottom: 20px;
}

.customer-info-section {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e4e7ed;
  overflow: hidden;
}

.customer-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7ed;
}

.customer-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid #409eff;
  box-shadow: 0 2px 8px rgba(64, 158, 255, 0.15);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.customer-avatar:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
}

.avatar-icon {
  font-size: 32px;
  color: #409eff;
}

.icon-helmet.avatar-icon {
  font-size: 34px;
  color: #409eff;
}

.avatar-image {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.customer-details {
  flex: 1;
  min-width: 0;
}

.customer-details h3 {
  margin: 0 0 6px 0;
  font-size: 20px;
  color: #303133;
  font-weight: 600;
}

.customer-phone {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 10px 0;
  color: #606266;
  font-size: 14px;
  text-decoration: none;
  transition: color 0.2s;
}

.customer-phone:hover {
  color: #409eff;
}

.customer-phone .el-icon {
  font-size: 14px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 16px;
  background: #fff;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #e4e7ed;
}

.status-badge .el-icon {
  font-size: 14px;
}

.status-badge.status-active {
  color: #67c23a;
  background: rgba(103, 194, 58, 0.1);
  border-color: rgba(103, 194, 58, 0.3);
}

.status-badge.status-active .el-icon {
  color: #67c23a;
}

.status-badge.status-inactive {
  color: #909399;
  background: rgba(144, 147, 153, 0.1);
  border-color: rgba(144, 147, 153, 0.3);
}

.status-badge.status-inactive .el-icon {
  color: #909399;
}

.status-badge.status-deleted {
  color: #f56c6c;
  background: rgba(245, 108, 108, 0.1);
  border-color: rgba(245, 108, 108, 0.3);
}

.status-badge.status-deleted .el-icon {
  color: #f56c6c;
}

.status-badge.status-blocked {
  color: #e6a23c;
  background: rgba(230, 162, 60, 0.1);
  border-color: rgba(230, 162, 60, 0.3);
}

.status-badge.status-blocked .el-icon {
  color: #e6a23c;
}

.vip-section {
  margin: 0;
  padding: 20px;
}

.vip-section .section-title {
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.vip-summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 8px;
  text-align: center;
}

.summary-label {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

.summary-value {
  font-size: 22px;
  color: #303133;
  font-weight: 700;
}

.summary-value.success {
  color: #67c23a;
}

.summary-value.price {
  color: #409eff;
}

.purchase-date {
  font-size: 13px;
  color: #606266;
}

.text-warning {
  color: #e6a23c;
  font-weight: 600;
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 16px;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
}

.section-title > div:first-child {
  display: flex;
  align-items: center;
  gap: 8px;
}

.vip-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.vip-card {
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.vip-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.vip-card-body {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.vip-info-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vip-info-row .label {
  font-size: 13px;
  color: #909399;
  font-weight: 500;
}

.vip-info-row .value {
  font-size: 15px;
  color: #303133;
  font-weight: 600;
}

.vip-info-row .value.price {
  color: #67c23a;
  font-size: 18px;
}

.vip-info-row .value.warning {
  color: #e6a23c;
}

.vip-card-footer {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}

.hours-remaining {
  text-align: center;
  margin-top: 8px;
  font-size: 14px;
  color: #606266;
  font-weight: 500;
}

.no-vip {
  margin: 40px 0;
}

.purchase-history {
  margin-top: 40px;
}

.desktop-table,
.mobile-cards {
  transition: all 0.3s ease;
}

.purchase-card {
  margin-bottom: 16px;
}

.purchase-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.purchase-package {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  flex: 1;
  margin-right: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.purchase-card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.purchase-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.purchase-info-row .label {
  font-size: 13px;
  color: #909399;
  font-weight: 500;
}

.purchase-info-row .value {
  font-size: 14px;
  color: #303133;
  font-weight: 600;
}

.purchase-info-row .value.price {
  color: #67c23a;
  font-size: 16px;
}

/* Mobile Responsive Styles */
@media screen and (max-width: 768px) {
  .page-container {
    padding: 10px;
  }
  
  .vip-detail {
    max-width: 100%;
  }
  
  .customer-main-content {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .customer-info-section {
    padding: 16px;
  }
  
  .customer-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
    padding: 16px;
  }
  
  .customer-avatar {
    width: 56px;
    height: 56px;
  }
  
  .avatar-icon {
    font-size: 28px;
  }
  
  .icon-helmet.avatar-icon {
    font-size: 30px;
  }
  
  .customer-details h3 {
    font-size: 18px;
  }
  
  .customer-phone {
    font-size: 13px;
  }
  
  .vip-section {
    padding: 16px;
  }
  
  .vip-summary-cards {
    grid-template-columns: 1fr;
    gap: 10px;
  }
  
  .summary-item {
    padding: 10px;
  }
  
  .summary-value {
    font-size: 20px;
  }
  
  .section-title {
    font-size: 16px;
    flex-wrap: wrap;
  }
  
  .section-title > div:first-child {
    flex: 1;
  }
  
  .purchase-history {
    margin-top: 24px;
  }
  
  .no-vip {
    margin: 24px 0;
  }
}

.error-message {
  text-align: center;
  padding: 60px 20px;
}

.loading-skeleton {
  padding: 20px;
}
</style>
